/**
 * Identity-stamped cache for the width-measurement patch grid.
 *
 * The geometric step fetches `api/exploration/width/results/data` and hands the
 * result to the Width page through localStorage so the page does not refetch on
 * mount. The payload used to be stored as a bare array under
 * `imageObjectsDownloaded`, with no record of which exploration or iteration it
 * described — so any leftover from an earlier exploration/iteration was replayed
 * verbatim. Every patch image then 404'd and the page showed
 * "Could not load patch data — Request failed with status code 404"
 * (production, 2026-08-21: 100 cached entries replayed against a 49-patch group).
 *
 * The cache is therefore stamped with its owner and validated on read. Anything
 * that does not match — legacy unstamped payloads included — is treated as a
 * miss, and the caller refetches from the server, which is authoritative.
 */

export const WIDTH_PATCH_CACHE_KEY = "imageObjectsDownloaded";

/** Normalise ids/iteration numbers to a comparable string. */
const stamp = (value) =>
  value === null || value === undefined ? "" : String(value);

/**
 * An entry is only usable if it carries a non-blank image path; a blank one is
 * a guaranteed 404 on `api/patch/file/get`.
 */
const hasUsableImagePath = (entry) =>
  !!entry &&
  typeof entry === "object" &&
  typeof entry.cluster_patch_image === "string" &&
  entry.cluster_patch_image.trim().length > 0;

/**
 * Build the value to persist. Returns a JSON string ready for localStorage.
 */
export function buildWidthPatchCache(explorationId, iterationNumber, results) {
  return JSON.stringify({
    explorationId: stamp(explorationId),
    iterationNumber: stamp(iterationNumber),
    results: Array.isArray(results) ? results : [],
  });
}

/**
 * Read a cached payload back.
 *
 * @returns the cached results array when it provably belongs to
 *   (explorationId, iterationNumber) and every entry is loadable; otherwise
 *   `null`, meaning "cache miss — refetch".
 */
export function readWidthPatchCache(raw, explorationId, iterationNumber) {
  if (raw === null || raw === undefined || raw === "") return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return null;
  }

  // Legacy shape: a bare array with no identity. It may well belong to another
  // exploration or iteration, so it can never be trusted.
  if (Array.isArray(parsed)) return null;

  if (!parsed || typeof parsed !== "object") return null;
  if (!Array.isArray(parsed.results)) return null;

  if (stamp(parsed.explorationId) !== stamp(explorationId)) return null;
  if (stamp(parsed.iterationNumber) !== stamp(iterationNumber)) return null;

  // An empty cache carries no information — refetch rather than render nothing.
  if (parsed.results.length === 0) return null;

  if (!parsed.results.every(hasUsableImagePath)) return null;

  return parsed.results;
}

/**
 * Apply one edited patch back into the cached payload.
 *
 * The page renders from the cache on every mount, so an edit that only lands
 * in React state is silently reverted the next time the user opens the step:
 * the corrected edges/classification would be replaced by the values the
 * automatic measurement had produced.
 *
 * @param raw   the current localStorage value (may be null/blank/invalid)
 * @param index position of the patch in the cached results array
 * @param patch the server-shaped fields to overwrite
 * @returns the new JSON string to store, or `null` when there is nothing
 *   usable to update (caller should leave localStorage untouched).
 */
export function updateWidthPatchCacheEntry(raw, index, patch) {
  if (raw === null || raw === undefined || raw === "") return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  if (!Array.isArray(parsed.results)) return null;
  if (!Number.isInteger(index) || index < 0 || index >= parsed.results.length) return null;

  const results = parsed.results.slice();
  results[index] = { ...results[index], ...patch };
  return JSON.stringify({ ...parsed, results });
}
